/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { ProgramType } from 'client-code/airing';
import PropTypes from 'prop-types';

import {
    CHANNEL_TITLE_MAX_LENGTH,
    PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
    STAR_RATING_MAX_STARS,
    UP_NEXT_TITLE_MAX_LENGTH,
} from '../../../config';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { PlaybackContentType } from '../../../core/playback/enums';
import { RootState, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { AiringPreviewUtils } from '../../../core/videoplayer/utils';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { Clock } from '../../Clock';
import { ClockTypes } from '../../Clock/ClockTypes';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { BannerTip } from '../BannerTip';
import { StandardInfoBannerProps } from './StandardInfoBannerProps';

import './StandardInfoBanner.scss';

const StandardInfoBanner: FunctionComponent<StandardInfoBannerProps> = (props): JSX.Element => {
    const {
        mAiringData,
        mPreviewData,
        hideBannerTipAtProgramBoundary,
        setHideBannerTipAtProgramBoundary,
        channel,
    } = props;
    const primaryBrandingImageUrl = useRef('');
    const channelNumber = channel?.channelNumber ?? '';
    const callSign = channel?.callSign ?? '';
    const channelLogoUrl = channel?.channelLogoUrl ?? '';
    const videoResolution = channel?.videoResolution ?? '';
    const enteringChannelNumberEnabled = useTivoSelector(
        (state: RootState) => state.infoBanner.enteringChannelNumberEnabled,
    );
    const { isTrickplayMenuActive, isFullInfoActive, isStandardInfoActive, channelNumberEntered } =
        useTivoSelector((state: RootState) => state.infoBanner);
    const [playbackTypeDisplayName, setPlaybackTypeDisplayName] = useState('');

    const isOnlyStandardInfoActive =
        !isTrickplayMenuActive && !isFullInfoActive && isStandardInfoActive;
    const standardInfoBannerClass = `standard-info-banner ${isOnlyStandardInfoActive ? 'only-standard-info-active' : ''}`;

    const isRecordingPlaying = VideoPlaybackController.getInstance().isNdvr();

    const playbackContentType =
        VideoPlaybackController.getInstance().getPlayable()?.playbackContentType;

    const isSocuContentPlaying = VideoPlaybackController.getInstance().isSocu();

    const dataFormatter = new AiringPreviewUtils(mAiringData, mPreviewData);
    const channelTitle = dataFormatter.getChannelTitleText();
    const channelTitleNext = dataFormatter.getChannelTitleTextNext();
    const isNew = dataFormatter.hasNew();
    const isNewNext = dataFormatter.hasNewNext();
    const seasonEpisode = dataFormatter.getSeasonEpisode();
    const time = isRecordingPlaying
        ? dataFormatter.getFormattedStartTime()
        : dataFormatter.getTime();
    const isMovieContent = dataFormatter.getProgramType() === ProgramType.Movie;

    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);

    const upNext = `${Locale.getString(
        LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_UP_NEXT,
    )} ${channelTitleNext}`;

    const renderPartnerBranding = () => {
        const socuImageType = ImageLoaderTypes.STANDARD_INFO_BANNER_PARTNER_BRANDING_WITH_SOCU_ICON;
        return (
            <>
                <ImageLoader
                    src={primaryBrandingImageUrl.current}
                    imageType={ImageLoaderTypes.STANDARD_INFO_BANNER_PARTNER_BRANDING}
                />
                {isSocuContentPlaying && <ImageLoader imageType={socuImageType} />}
            </>
        );
    };

    const renderTopRow = () => {
        const channelTitleClasses = `channel-title ${
            channelTitle.length > CHANNEL_TITLE_MAX_LENGTH ? 'truncate' : ''
        }`;
        return (
            <div className="standard-info-top-row">
                <div
                    className={channelTitleClasses}
                    data-testid="standard-info-banner-channel-title"
                >
                    {channelTitle}
                </div>
                <div className="playback-type">
                    <span data-testid="standard-info-banner-metadata-separator"> | </span>
                    {playbackTypeDisplayName}
                </div>
                {isNew && (
                    <img
                        src={newIcon?.imageUrl}
                        className={`new-icon ${newIcon?.imageType}`}
                        data-testid={'standard-info-banner-new-icon'}
                    />
                )}
                <div className="channel-info">
                    {channelNumber && !enteringChannelNumberEnabled ? (
                        <div
                            className="channel-number"
                            data-testid="standard-info-banner-channel-number"
                        >
                            {channelNumber}
                        </div>
                    ) : (
                        <span className="enter-channel-number">{channelNumberEntered}_</span>
                    )}
                    {videoResolution && (
                        <div
                            className={`stream-type-icon ${videoResolution}`}
                            data-testid="standard-info-banner-video-resolution"
                        ></div>
                    )}
                    <ImageLoader
                        src={channelLogoUrl}
                        imageType={ImageLoaderTypes.STANDARD_INFO_BANNER_CHANNEL_BRANDING}
                        placeholder={callSign}
                    />
                </div>
                <div className="clock-container">
                    <Clock
                        type={ClockTypes.WATCH_VIDEO_CLOCK}
                        data-testid="standard-info-banner-clock"
                    />
                </div>
            </div>
        );
    };

    const renderBottomRow = () => {
        return (
            <div className="standard-info-banner-bottom-row">
                {renderFullInfoMetadata()}
                {renderUpNextData()}
            </div>
        );
    };

    const renderFullInfoMetadata = () => {
        const isSeparatorRequired = !isMovieContent
            ? dataFormatter.getParentalControlsRatingSeasonEpisodeSeperator()
            : dataFormatter.getParentalControlsRatingStarRatingSeperator();
        return (
            <div className="metadata" data-testid="standard-info-banner-channel-metadata">
                <span data-testid="standard-info-banner-time">{time}</span>
                {renderMetadataSeparator(dataFormatter.getTimeParentalControlsRatingSeperator())}
                {renderParentalControlsRating()}
                {renderMetadataSeparator(isSeparatorRequired)}
                {!isMovieContent ? renderSeasonEpisode() : renderStarRating()}
            </div>
        );
    };

    const renderMetadataSeparator = (check: boolean) => {
        return check && <span data-testid="standard-info-banner-metadata-separator"> | </span>;
    };

    const renderParentalControlsRating = () => {
        const parentalControlsRating = dataFormatter.getParentalControlsRating();
        return parentalControlsRating?.map(({ typeId, valueId }: any, index: number) => {
            const ratingName = services.getInternalRatingName(typeId + valueId);
            return ratingName ? (
                <span
                    className="parental-controls-rating-text"
                    key={index}
                    data-testid="standard-info-banner-parental-ratings"
                >
                    {ratingName}
                </span>
            ) : null;
        });
    };

    const renderSeasonEpisode = () => {
        return (
            seasonEpisode && (
                <span data-testid="standard-info-banner-season-episode">{seasonEpisode}</span>
            )
        );
    };

    const renderStarRating = () => {
        const starRating = dataFormatter.getStarRating();

        return starRating !== 0 ? (
            <span className="star-rating" data-testid="standard-info-banner-star-rating">
                {new Array(Math.floor(starRating)).fill(0).map((_, index) => (
                    <span className="star-icon full" key={index}></span>
                ))}
                {starRating > Math.floor(starRating) ? (
                    <span className="star-icon half"></span>
                ) : null}
                {new Array(STAR_RATING_MAX_STARS - Math.floor(starRating))
                    .fill(0)
                    .map((_, index) => (
                        <span className="star-icon empty" key={index}></span>
                    ))}
            </span>
        ) : null;
    };

    const renderUpNextData = () => {
        const upNextTrunactionClass = isNewNext ? 'truncate-up-next-with-new' : 'truncate-up-next';
        const upNextClasses = `up-next ${
            upNext.length > UP_NEXT_TITLE_MAX_LENGTH ? upNextTrunactionClass : ''
        }`;
        return !isSocuContentPlaying && !isRecordingPlaying ? (
            <div className="up-next-data">
                <div className={upNextClasses} data-testid="standard-info-banner-up-next">
                    {upNext}
                </div>
                {isNewNext && (
                    <img
                        src={newIcon?.imageUrl}
                        className={`next-new-icon ${newIcon?.imageType}`}
                        data-testid={'standard-info-banner-next-new-icon'}
                    />
                )}
            </div>
        ) : null;
    };

    useEffect(() => {
        switch (playbackContentType) {
            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.STARTOVER_RESUME:
                setPlaybackTypeDisplayName(
                    services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER),
                );
                break;
            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
                setPlaybackTypeDisplayName(
                    services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                );
                break;
            case PlaybackContentType.NDVR_LIVE:
            case PlaybackContentType.NDVR_RECORDED:
                setPlaybackTypeDisplayName(
                    Locale.getString(LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_RECORDING_STRING),
                );
                break;
            default:
                setPlaybackTypeDisplayName(
                    Locale.getString(LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_LIVE_TV_STRING),
                );
                break;
        }

        primaryBrandingImageUrl.current = services.getPartnerBrandingImageUrl(
            PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
            PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
        );
        return () => {
            if (hideBannerTipAtProgramBoundary) setHideBannerTipAtProgramBoundary(false);
        };
    }, [playbackContentType]);

    return (
        <div className="standard-info-banner-container">
            <BannerTip
                hideBannerTipAtProgramBoundary={hideBannerTipAtProgramBoundary}
                previewData={mPreviewData}
            />
            <div className={standardInfoBannerClass} data-testid="standard-info-banner">
                {renderPartnerBranding()}
                {renderTopRow()}
                {renderBottomRow()}
            </div>
        </div>
    );
};

StandardInfoBanner.propTypes = {
    mAiringData: PropTypes.any.isRequired,
    mPreviewData: PropTypes.any.isRequired,
    hideBannerTipAtProgramBoundary: PropTypes.any.isRequired,
    setHideBannerTipAtProgramBoundary: PropTypes.any.isRequired,
    channel: PropTypes.any,
};

export default StandardInfoBanner;
